// src/pages/CheckInOutPage.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import {
  Card,
  Button,
  Space,
  Typography,
  Table,
  Tag,
  Input,
  Form,
  DatePicker,
  Modal,
  Alert,
  notification,
  Badge,
  Descriptions,
  Divider,
} from "antd";
import {
  FaSearch,
  FaUserCheck,
  FaFileInvoiceDollar,
  FaSyncAlt,
  FaInfoCircle,
} from "react-icons/fa";
import Breadcrumb from "../components/Breadcrumb/Breadcrumb";
import { useReactToPrint } from "react-to-print";
import InvoiceModal from "../components/InvoiceModal";
import moment from "moment";
import "./CheckInOutPage.css";

const { Text, Title } = Typography;

const num = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

const str = (v) => (v == null ? "" : String(v));

const pick = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return undefined;
};

const CheckInOutPage = () => {
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  const [form] = Form.useForm();
  const invoiceRef = useRef(null);

  const [hotelDetails, setHotelDetails] = useState({
    hotelName: "",
    address: "",
    phoneNumber: "",
  });

  const [guestDetails, setGuestDetails] = useState({
    name: "",
    firstName: "",
    lastName: "",
    notes: "",
    roomNo: "",
    bookingNo: "",
    bookingId: 0,
    email: "",
    mobile: "",
    address: "",
    timeFormat: "24HRS",
    pendingAmount: 0,
    paymentAmount: 0,
    basePrice: 0,
    vatPercentage: 0,
    additionalLevies: 0,
    daysBooked: 1,
    totalAmount: 0,
    transactionType: "",
    currency: "BWP",
  });

  const [bookings, setBookings] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusMessage, setStatusMessage] = useState({ type: "", content: "" });

  const [loading, setLoading] = useState({
    search: false,
    checkins: false,
    checkIn: false,
    checkOut: false,
  });

  const [modalVisible, setModalVisible] = useState({
    checkIn: false,
    checkOut: false,
    invoice: false,
  });

  const [dates, setDates] = useState({
    checkIn: null,
    checkOut: null,
  });

  const getStatusColor = (status) => {
    const statusMap = {
      CheckedIn: "green",
      CheckedOut: "volcano",
      Reserved: "blue",
      Cancelled: "red",
      NoShow: "orange",
      Pending: "gold",
      Confirmed: "blue",
    };
    return statusMap[status] || "gray";
  };

  const showApiError = (error, fallback = "Request failed. Please try again.") => {
    // AntD form validation errors
    if (error?.errorFields?.length) {
      const first = error.errorFields[0];
      notification.error({
        message: "Validation Error",
        description: first?.errors?.[0] || "Please check the form fields.",
      });
      return;
    }

    console.error("API Error:", error);

    const apiErrors = error?.response?.data?.errors;
    const apiMessage =
      error?.response?.data?.message ||
      (Array.isArray(apiErrors) ? apiErrors.join(", ") : null) ||
      error?.message ||
      fallback;

    notification.error({
      message: "Error",
      description: apiMessage,
    });
  };

  // Fetch hotel details
  useEffect(() => {
    const fetchHotelDetails = async () => {
      if (!hotelId) return;
      try {
        const response = await axiosInstance.get(`/Hotel/${hotelId}`);
        setHotelDetails({
          hotelName: response.data?.hotelName || "Hotel Name",
          address: response.data?.address || "Hotel Address",
          phoneNumber: response.data?.contactNumber || "+000 000 0000",
        });
      } catch (error) {
        console.error("Error fetching hotel details:", error);
      }
    };
    fetchHotelDetails();
  }, [hotelId]);

  // Fetch current check-ins
  const fetchCheckins = async () => {
    if (!hotelId) {
      notification.error({
        message: "Error",
        description: "Hotel ID is missing. Please log in again.",
      });
      return;
    }

    setLoading((prev) => ({ ...prev, checkins: true }));
    try {
      const response = await axiosInstance.get(`/hotels/${hotelId}/Status/checkedin`);
      setCheckins(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      showApiError(error, "Failed to fetch check-ins.");
    } finally {
      setLoading((prev) => ({ ...prev, checkins: false }));
    }
  };

  useEffect(() => {
    fetchCheckins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId]);

  // Search guest handler
  const handleSearchGuest = async () => {
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      notification.warning({ message: "Warning", description: "Please enter a valid ID Number." });
      return;
    }
    if (!hotelId) {
      notification.error({ message: "Error", description: "Hotel ID is missing. Please log in again." });
      return;
    }

    setLoading((prev) => ({ ...prev, search: true }));
    setStatusMessage({ type: "", content: "" });

    try {
      const [guestResponse, bookingsResponse] = await Promise.all([
        axiosInstance.get("/Guests/SearchByIdNumberAndHotel", {
          params: { idNumber: trimmed, hotelId },
        }),
        axiosInstance.get("/Bookings/SearchBookingsByGuest", {
          params: {
            idNumber: trimmed,
            hotelId,
            includeStatuses: [1], // Confirmed=1 (adjust to your enum)
          },
        }),
      ]);

      const guest = guestResponse.data;
      const rawBookings = Array.isArray(bookingsResponse.data)
        ? bookingsResponse.data
        : bookingsResponse.data
          ? [bookingsResponse.data]
          : [];

      if (!guest) {
        notification.warning({ message: "Not Found", description: "Guest not found." });
        return;
      }

      if (!rawBookings.length) {
        notification.warning({ message: "No Bookings", description: "No bookings found for the guest." });
        return;
      }

      const firstBooking = rawBookings[0];

      // STRONGER fallback mapping for BookingId/BookingNo/RoomId
      const bookingId = num(
        pick(
          firstBooking.bookingId,
          firstBooking.BookingId,
          firstBooking.bookingID,
          firstBooking.BookingID,
          firstBooking.id,
          firstBooking.Id
        ),
        0
      );

      const bookingNo = str(
        pick(firstBooking.bookingNo, firstBooking.BookingNo, firstBooking.bookingNumber, firstBooking.BookingNumber)
      );

      const roomId = num(pick(firstBooking.roomId, firstBooking.RoomId, firstBooking.roomID, firstBooking.RoomID), 0);

      // Try to get room number from booking first, else fetch room
      let roomNumber = str(pick(firstBooking.roomNumber, firstBooking.RoomNumber));
      if (!roomNumber && roomId > 0) {
        const roomRes = await axiosInstance.get(`/Rooms/${roomId}`);
        roomNumber = str(pick(roomRes.data?.roomNumber, roomRes.data?.RoomNumber, "Unknown"));
      }

      const totalAmount = num(pick(firstBooking.totalAmount, firstBooking.TotalAmount), 0);
      const paidAmount = num(pick(firstBooking.paidAmount, firstBooking.PaidAmount), 0);
      const pendingAmount = Math.max(totalAmount - paidAmount, 0);

      const currency = str(pick(firstBooking.currency, firstBooking.Currency, "BWP"));

      setGuestDetails({
        name: `${str(guest.firstName)} ${str(guest.lastName)}`.trim(),
        firstName: str(guest.firstName),
        lastName: str(guest.lastName),
        notes: "",
        roomNo: roomNumber || "Unknown",
        bookingNo,
        bookingId,
        email: str(guest.email),
        mobile: guest.phoneNumber?.trim() ? str(guest.phoneNumber) : "70000000",
        address: str(guest.address),
        pendingAmount,
        timeFormat: "24HRS",
        paymentAmount: 0,
        basePrice: num(pick(firstBooking.basePrice, firstBooking.BasePrice), 0),
        vatPercentage: num(pick(firstBooking.vatPercentage, firstBooking.VatPercentage), 0),
        additionalLevies: num(pick(firstBooking.additionalLevies, firstBooking.AdditionalLevies), 0),
        daysBooked: num(pick(firstBooking.daysBooked, firstBooking.DaysBooked), 1),
        totalAmount,
        transactionType: str(pick(firstBooking.transactionType, firstBooking.TransactionType)),
        currency,
      });

      setBookings(rawBookings);

      setDates({
        checkIn: firstBooking.checkInDate ? moment(firstBooking.checkInDate) : null,
        checkOut: firstBooking.checkOutDate ? moment(firstBooking.checkOutDate) : null,
      });

      // Make sure modal form values are reset (initialValues won't update on its own)
      form.setFieldsValue({ notes: "", paymentAmount: 0 });

      notification.success({ message: "Success", description: "Guest and booking details fetched successfully." });

      if (bookingId <= 0) {
        notification.warning({
          message: "Missing BookingId",
          description:
            "Your bookings endpoint did not return BookingId. Check-in will fail until BookingId is included or backend validation is relaxed.",
        });
      }
    } catch (error) {
      showApiError(error, "Error fetching details. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, search: false }));
    }
  };

  const openCheckInModal = () => {
    form.setFieldsValue({
      notes: guestDetails.notes || "",
      paymentAmount: guestDetails.paymentAmount || 0,
    });
    setModalVisible((prev) => ({ ...prev, checkIn: true }));
  };

  // ✅ FIXED: send full DTO required by backend validation
  const handleCheckIn = async () => {
    setLoading((prev) => ({ ...prev, checkIn: true }));
    try {
      const values = await form.validateFields();

      const bookingId = num(guestDetails.bookingId, 0);
      const bookingNo = str(guestDetails.bookingNo).trim();

      if (!bookingNo) {
        notification.error({
          message: "Missing Booking Number",
          description: "BookingNo is empty. Please search the guest again.",
        });
        return;
      }

      if (bookingId <= 0) {
        notification.error({
          message: "Invalid Booking ID",
          description:
            "BookingId must be greater than zero. Your bookings search API must return BookingId (or relax backend validation).",
        });
        return;
      }

      const pending = num(guestDetails.pendingAmount, 0);
      const payment = num(values.paymentAmount, 0);

      if (pending > 0 && payment < pending) {
        notification.error({
          message: "Payment Required",
          description: `Full payment is required to check-in. Pending: ${guestDetails.currency}${pending.toFixed(2)}`,
        });
        return;
      }

      // Keep local state in sync
      setGuestDetails((prev) => ({
        ...prev,
        paymentAmount: payment,
        notes: values.notes || "",
      }));

      // IMPORTANT: match your backend CheckInRequest validations
      const payload = {
        // These were required by your validation error list
        Name: guestDetails.name,
        FirstName: guestDetails.firstName,
        LastName: guestDetails.lastName,
        Email: guestDetails.email,
        Mobile: guestDetails.mobile,
        RoomNo: guestDetails.roomNo,
        Address: guestDetails.address,
        TimeFormat: guestDetails.timeFormat,

        // These are critical for server logic
        BookingId: bookingId,
        BookingNo: bookingNo,
        PaymentAmount: payment,
        Notes: values.notes || "",

        // Optional dates (include if your DTO has them)
        CheckInDate: dates.checkIn ? dates.checkIn.toISOString() : null,
        CheckOutDate: dates.checkOut ? dates.checkOut.toISOString() : null,

        // Your server code uses ExpectedCheckOutTime for extension logic
        ExpectedCheckOutTime: dates.checkOut ? dates.checkOut.toISOString() : null,
      };

      const res = await axiosInstance.post("/CheckIn", payload);

      notification.success({
        message: "Success",
        description: res?.data?.message || "Guest successfully checked in.",
      });

      setModalVisible((prev) => ({ ...prev, checkIn: false }));

      await fetchCheckins();
      await handleSearchGuest();
    } catch (error) {
      showApiError(error, "Check-in failed. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, checkIn: false }));
    }
  };

  const handleViewInvoice = async (bookingId) => {
    try {
      const response = await axiosInstance.get(`/CheckIn/${bookingId}/invoice`);
      const invoiceData = response.data;

      setGuestDetails((prev) => ({
        ...prev,
        ...invoiceData,
        checkInDate: invoiceData.checkInDate,
        checkOutDate: invoiceData.checkOutDate,
      }));

      setModalVisible((prev) => ({ ...prev, invoice: true }));
    } catch (error) {
      showApiError(error, "Failed to fetch invoice data.");
    }
  };

  const handlePrint = useReactToPrint({
    content: () => invoiceRef.current,
    documentTitle: `Invoice_${guestDetails.bookingNo}`,
    onAfterPrint: () => notification.success({ message: "Invoice printed successfully" }),
  });

  const guestColumns = useMemo(
    () => [
      {
        title: "Booking ID",
        dataIndex: "bookingId",
        key: "bookingId",
        render: (id, record) => id || record?.BookingId || record?.bookingID || "N/A",
      },
      {
        title: "Room Number",
        dataIndex: "roomNumber",
        key: "roomNumber",
        render: (text, record) => text || record?.RoomNumber || "Unknown",
      },
      {
        title: "Status",
        key: "status",
        render: (_, record) => (
          <Tag color={getStatusColor(record.status || record.Status)}>
            {record.status || record.Status || "Unknown"}
          </Tag>
        ),
      },
    ],
    []
  );

  const checkinColumns = useMemo(
    () => [
      {
        title: "Booking ID",
        dataIndex: "bookingId",
        key: "bookingId",
        render: (id) => id || "N/A",
      },
      {
        title: "Guest Name",
        key: "name",
        render: (_, record) => `${record.firstName || ""} ${record.lastName || ""}`.trim() || "N/A",
      },
      {
        title: "Room Number",
        dataIndex: "roomNumber",
        key: "roomNumber",
        render: (text) => text || "Unknown",
      },
      {
        title: "Check-In Date",
        key: "checkInDate",
        render: (_, record) => (record.checkInDate ? moment(record.checkInDate).format("LLL") : "N/A"),
      },
      {
        title: "Paid Amount",
        key: "paidAmount",
        render: (_, record) => `${guestDetails.currency || "BWP"}${num(record.paidAmount, 0).toFixed(2)}`,
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => (
          <Space>
            <Button icon={<FaFileInvoiceDollar />} onClick={() => handleViewInvoice(record.bookingId)}>
              Invoice
            </Button>
          </Space>
        ),
      },
    ],
    [guestDetails.currency]
  );

  return (
    <div className="checkin-page">
      <Breadcrumb />

      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              Guest Check-In
            </Title>
            <Badge count={checkins.length} showZero style={{ backgroundColor: "#1890ff" }} />
            <Button
              type="text"
              icon={<FaInfoCircle />}
              onClick={() =>
                notification.info({
                  message: "Info",
                  description: "Search guests by ID to manage check-ins and check-outs.",
                })
              }
            />
          </Space>
        }
        extra={
          <Space>
            <Button icon={<FaSyncAlt />} onClick={fetchCheckins} loading={loading.checkins}>
              Refresh
            </Button>
          </Space>
        }
      >
        {statusMessage?.content && (
          <Alert style={{ marginBottom: 16 }} type={statusMessage.type || "info"} message={statusMessage.content} showIcon />
        )}

        <Card title="Search Guest" size="small" style={{ marginBottom: 16 }}>
          <Space.Compact style={{ width: "100%" }}>
            <Input
              placeholder="Enter Guest ID Number"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              maxLength={10}
              allowClear
              onPressEnter={handleSearchGuest}
            />
            <Button type="primary" icon={<FaSearch />} onClick={handleSearchGuest} loading={loading.search}>
              Search
            </Button>
          </Space.Compact>
        </Card>

        {guestDetails.name && (
          <Card
            title="Guest Details"
            size="small"
            style={{ marginBottom: 16 }}
            extra={
              <Button type="primary" icon={<FaUserCheck />} onClick={openCheckInModal}>
                Check-In
              </Button>
            }
          >
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Name">{guestDetails.name}</Descriptions.Item>
              <Descriptions.Item label="Email">{guestDetails.email}</Descriptions.Item>
              <Descriptions.Item label="Mobile">{guestDetails.mobile}</Descriptions.Item>
              <Descriptions.Item label="Address">{guestDetails.address}</Descriptions.Item>
              <Descriptions.Item label="Room Number">{guestDetails.roomNo}</Descriptions.Item>
              <Descriptions.Item label="Booking Number">{guestDetails.bookingNo}</Descriptions.Item>
              <Descriptions.Item label="Pending Amount">
                <Text type="danger">
                  {guestDetails.currency}
                  {num(guestDetails.pendingAmount, 0).toFixed(2)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="BookingId">
                <Text type={guestDetails.bookingId > 0 ? "success" : "danger"}>{guestDetails.bookingId || 0}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {bookings.length > 0 && (
          <Card title="Guest Bookings" size="small" style={{ marginBottom: 16 }}>
            <Table
              columns={guestColumns}
              dataSource={bookings}
              rowKey={(r) => r.bookingId || r.BookingId || r.id || r.Id || Math.random()}
              pagination={false}
              size="small"
            />
          </Card>
        )}

        <Card title="Current Check-Ins" size="small">
          <Table
            columns={checkinColumns}
            dataSource={checkins}
            rowKey="bookingId"
            loading={loading.checkins}
            pagination={{ pageSize: 5 }}
            size="small"
            locale={{ emptyText: "No current check-ins found" }}
          />
        </Card>

        <Modal
          title="Check-In Guest"
          open={modalVisible.checkIn}
          onCancel={() => setModalVisible((prev) => ({ ...prev, checkIn: false }))}
          footer={[
            <Button key="cancel" onClick={() => setModalVisible((prev) => ({ ...prev, checkIn: false }))}>
              Cancel
            </Button>,
            <Button key="submit" type="primary" loading={loading.checkIn} onClick={handleCheckIn}>
              Confirm Check-In
            </Button>,
          ]}
          width={800}
          destroyOnClose
        >
          <Form form={form} layout="vertical" initialValues={{ notes: guestDetails.notes, paymentAmount: guestDetails.paymentAmount }}>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Name">{guestDetails.name}</Descriptions.Item>
              <Descriptions.Item label="Room Number">{guestDetails.roomNo}</Descriptions.Item>
              <Descriptions.Item label="Booking Number">{guestDetails.bookingNo}</Descriptions.Item>
              <Descriptions.Item label="Pending Amount">
                <Text type="danger">
                  {guestDetails.currency}
                  {num(guestDetails.pendingAmount, 0).toFixed(2)}
                </Text>
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Form.Item label="Notes" name="notes" rules={[{ required: true, message: "Please enter notes" }]}>
              <Input.TextArea rows={3} placeholder="Enter any special requests or notes" />
            </Form.Item>

            <Form.Item
              label="Payment Amount"
              name="paymentAmount"
              rules={[
                { required: num(guestDetails.pendingAmount, 0) > 0, message: "Payment is required" },
                {
                  validator: (_, value) => {
                    const pending = num(guestDetails.pendingAmount, 0);
                    const v = num(value, 0);
                    if (pending > 0 && v < pending) return Promise.reject(new Error("Amount must cover pending balance"));
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input type="number" prefix={guestDetails.currency} min={num(guestDetails.pendingAmount, 0)} step="0.01" />
            </Form.Item>

            <Form.Item label="Check-In Date/Time">
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                value={dates.checkIn}
                onChange={(date) => setDates((prev) => ({ ...prev, checkIn: date }))}
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item label="Check-Out Date/Time">
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                value={dates.checkOut}
                onChange={(date) => setDates((prev) => ({ ...prev, checkOut: date }))}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Form>
        </Modal>

        <InvoiceModal
          isOpen={modalVisible.invoice}
          onClose={() => setModalVisible((prev) => ({ ...prev, invoice: false }))}
          guestDetails={guestDetails}
          hotelDetails={hotelDetails}
          ref={invoiceRef}
          onPrint={handlePrint}
        />
      </Card>
    </div>
  );
};

export default CheckInOutPage;
